import React, { useState } from 'react';
import { TrajectoryChart } from './TrajectoryChart';
import { TimelineControls } from './TimelineControls';
import { useBasicTrajectory } from '../../hooks/useBasicPredictions';
import { BASIC_CATEGORIES, type BasicId } from '../../types/predictions';
import { TrendingUp, TrendingDown, Minus, AlertCircle, Clock, Lightbulb } from 'lucide-react';

interface TrajectoryVisualizationProps {
  dotNumber: number;
}

/**
 * TrajectoryVisualization Component
 * Main container for BASIC score trajectory visualization
 *
 * Features:
 * - BASIC selector (11, 12, 13, 15)
 * - Timeline controls integration
 * - Trajectory chart display
 * - Summary cards (trend, aging violations, projections)
 * - Insights and recommendations
 * - Loading/error states
 */
export const TrajectoryVisualization: React.FC<TrajectoryVisualizationProps> = ({
  dotNumber,
}) => {
  const [selectedBasic, setSelectedBasic] = useState<string>('BASIC-11');
  const [months, setMonths] = useState<number>(6);

  const { trajectory, loading, error } = useBasicTrajectory(dotNumber, selectedBasic, months);

  const basicCategories = (Object.keys(BASIC_CATEGORIES) as BasicId[]).map((id) => ({
    id,
    name: BASIC_CATEGORIES[id].name,
    threshold: BASIC_CATEGORIES[id].threshold,
  }));

  const selectedCategory = basicCategories.find((b) => b.id === selectedBasic);

  // Trend icon helper
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'IMPROVING':
        return <TrendingDown className="w-6 h-6 text-green-600" />;
      case 'DECLINING':
        return <TrendingUp className="w-6 h-6 text-red-600" />;
      default:
        return <Minus className="w-6 h-6 text-blue-600" />;
    }
  };

  // Trend color helper
  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'IMPROVING':
        return 'text-green-600';
      case 'DECLINING':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="bg-purple-50 rounded-lg shadow p-6 border border-purple-100">
        <div className="flex items-center gap-3 mb-3">
          <TrendingUp className="w-8 h-8 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-900">BASIC Score Trajectory</h2>
        </div>
        <p className="text-gray-600 mb-4">
          Project how your BASIC scores will change over time as violations age out of the 24-month window.
        </p>

        {/* BASIC Selector */}
        <div className="flex flex-wrap gap-2">
          {basicCategories.map((basic) => (
            <button
              key={basic.id}
              onClick={() => setSelectedBasic(basic.id)}
              className={`px-4 py-2 rounded-lg transition-all duration-200 font-medium ${
                selectedBasic === basic.id
                  ? 'bg-purple-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-purple-50 hover:shadow border border-gray-300'
              }`}
            >
              <div className="text-sm font-semibold">{basic.id}</div>
              <div className="text-xs opacity-90">{basic.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Timeline Controls */}
      <TimelineControls currentMonths={months} onMonthsChange={setMonths} />

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading trajectory data...</p>
          <p className="text-sm text-gray-400 mt-1">Calculating {months}-month projection</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg shadow">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Error loading trajectory</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Chart and Results */}
      {!loading && !error && trajectory && (
        <>
          {/* Trajectory Chart */}
          <TrajectoryChart
            data={trajectory.trajectory || []}
            threshold={selectedCategory?.threshold || 65}
            basicId={selectedBasic}
            currentPercentile={trajectory.currentPercentile ?? null}
          />

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Trend Card */}
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-600 uppercase">Trend</p>
                {getTrendIcon(trajectory.trend || 'STABLE')}
              </div>
              <p className={`text-2xl font-bold ${getTrendColor(trajectory.trend || 'STABLE')}`}>
                {trajectory.trend || 'STABLE'}
              </p>
              {trajectory.estimatedImprovement != null && trajectory.estimatedImprovement !== 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {trajectory.estimatedImprovement > 0 ? '+' : ''}
                  {trajectory.estimatedImprovement?.toFixed(0) || 0} percentile points
                </p>
              )}
            </div>

            {/* Violations Aging Out Card */}
            <div className="bg-green-50 rounded-lg shadow p-5 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-green-700 uppercase">Aging Out</p>
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-600">
                {(trajectory.trajectory || []).reduce((sum, point) => sum + (point.violationsAgedOut || 0), 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                violations in {trajectory.projectionMonths || 6} months
              </p>
            </div>

            {/* Current Score Card */}
            <div className="bg-blue-50 rounded-lg shadow p-5 border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-blue-700 uppercase">Current Score</p>
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {trajectory.currentPercentile !== null ? `${trajectory.currentPercentile}th` : 'N/A'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Threshold: {selectedCategory?.threshold}th percentile
              </p>
            </div>
          </div>

          {/* Insights and Recommendations */}
          {trajectory.recommendations && trajectory.recommendations.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 shadow">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    Insights & Recommendations
                  </h3>
                  <ul className="space-y-2">
                    {trajectory.recommendations.map((recommendation, index) => (
                      <li key={index} className="text-sm text-amber-800 flex items-start gap-2">
                        <span className="text-amber-600 font-bold">•</span>
                        <span>{recommendation}</span>
                      </li>
                    ))}
                    {trajectory.trend === 'IMPROVING' && (
                      <li className="text-sm text-amber-800 flex items-start gap-2">
                        <span className="text-amber-600 font-bold">•</span>
                        <span>Continue current safety practices to maintain improvement trend</span>
                      </li>
                    )}
                    {trajectory.currentPercentile !== null &&
                      trajectory.currentPercentile < selectedCategory!.threshold && (
                        <li className="text-sm text-amber-800 flex items-start gap-2">
                          <span className="text-amber-600 font-bold">•</span>
                          <span>
                            Score is currently below threshold - excellent safety performance
                          </span>
                        </li>
                      )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Projection Details */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm text-gray-600">
            <p className="font-semibold mb-2">About This Projection:</p>
            <ul className="space-y-1 ml-4 list-disc">
              <li>
                Showing {trajectory.projectionMonths || 6}-month projection for {selectedBasic} ({selectedCategory?.name})
              </li>
              <li>Projections assume no new violations are added during this period</li>
              <li>Scores naturally improve as violations age out of the 24-month calculation window</li>
              <li>
                Current measure: {trajectory.currentMeasure?.toFixed(4) || 'N/A'} | Percentile:{' '}
                {trajectory.currentPercentile != null ? `${trajectory.currentPercentile}th` : 'N/A'}
              </li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default TrajectoryVisualization;
